import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = '/api';

const PublicAmbassadorDashboard: React.FC = () => {
  const { campaignSlug, ambassadorSlug } = useParams<{ campaignSlug: string; ambassadorSlug: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // ── Goal edit (SMS OTP) ───────────────────────────────────────────────────
  // Two steps: type the new goal → we SMS a code to the phone the org has on
  // file → type the code. The phone is deliberately not editable here; only
  // the org's admins can change it, which is what makes the code meaningful.
  const [goalOpen, setGoalOpen] = useState(false);
  const [goalDraft, setGoalDraft] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpPhoneMasked, setOtpPhoneMasked] = useState('');
  const [goalBusy, setGoalBusy] = useState(false);
  const [goalError, setGoalError] = useState<string | null>(null);
  const [goalDone, setGoalDone] = useState(false);

  useEffect(() => {
    if (!campaignSlug || !ambassadorSlug) return;
    axios.get(`${API_BASE}/public/ambassador-dashboard/${campaignSlug}/${ambassadorSlug}`)
      .then(res => setData(res.data))
      .catch(() => setError('לא נמצא שגריר'))
      .finally(() => setLoading(false));
  }, [campaignSlug, ambassadorSlug]);

  const openGoalEditor = () => {
    setGoalDraft(String(data?.ambassador?.goal || ''));
    setOtpCode(''); setOtpSent(false); setOtpPhoneMasked(''); setGoalError(null); setGoalDone(false);
    setGoalOpen(true);
  };

  const requestOtp = async () => {
    const goalNum = Math.floor(Number(goalDraft));
    if (!Number.isFinite(goalNum) || goalNum < 0) { setGoalError('יעד לא תקין'); return; }
    setGoalBusy(true); setGoalError(null);
    try {
      const res = await axios.post(`${API_BASE}/public/ambassador-dashboard/${campaignSlug}/${ambassadorSlug}/goal-otp`, {});
      setOtpPhoneMasked(res.data?.phoneMasked || ambassador.phoneMasked || '');
      setOtpSent(true);
    } catch (e: any) {
      setGoalError(e?.response?.data?.error || 'שליחת הקוד נכשלה');
    } finally { setGoalBusy(false); }
  };

  const confirmGoal = async () => {
    setGoalBusy(true); setGoalError(null);
    try {
      const res = await axios.post(`${API_BASE}/public/ambassador-dashboard/${campaignSlug}/${ambassadorSlug}/goal`, {
        goal: Math.floor(Number(goalDraft)), code: otpCode,
      });
      setData((d: any) => ({ ...d, ambassador: { ...d.ambassador, goal: res.data.goal, goalSetByAmbassador: true } }));
      setGoalDone(true);
      setTimeout(() => setGoalOpen(false), 1200);
    } catch (e: any) {
      setGoalError(e?.response?.data?.error || 'עדכון היעד נכשל');
    } finally { setGoalBusy(false); }
  };

  if (loading) return (
    <div dir="rtl" style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Heebo', sans-serif" }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, border: '4px solid #e5e7eb', borderTop: '4px solid #f59e0b', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ color: '#6b7280' }}>טוען דאשבורד...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error || !data) return (
    <div dir="rtl" style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Heebo', sans-serif" }}>
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>😔</div>
        <p style={{ fontSize: 18, color: '#374151' }}>{error || 'שגיאה'}</p>
      </div>
    </div>
  );

  const { ambassador, campaign, orgName, shareUrl, donations, group } = data;
  const pct = ambassador.goal > 0 ? Math.round((ambassador.totalRaised / ambassador.goal) * 100) : 0;
  const groupPct = group && group.goal > 0 ? Math.round((group.raised / group.goal) * 100) : 0;
  const primary = '#f59e0b';

  return (
    <div dir="rtl" style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #fffbeb 0%, #f8fafc 40%)', fontFamily: "'Heebo', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700;800;900&display=swap');
      `}</style>

      <div style={{ maxWidth: 500, margin: '0 auto', padding: '24px 16px' }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b, #f97316)',
          borderRadius: 20, padding: '28px 24px', textAlign: 'center', color: '#fff',
          boxShadow: '0 8px 32px rgba(245,158,11,0.3)', marginBottom: 20,
        }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🤝</div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>{ambassador.name}</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, opacity: 0.9 }}>שגריר/ה בקמפיין "{campaign.name}"</p>
          <p style={{ margin: '4px 0 0', fontSize: 12, opacity: 0.7 }}>{orgName}</p>
        </div>

        {/* Progress + personal goal */}
        <div style={{
          background: '#fff', borderRadius: 16, padding: 24, marginBottom: 16,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #f3f4f6',
        }}>
          {ambassador.goal > 0 ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14, fontWeight: 700 }}>
                <span style={{ color: primary }}>₪{ambassador.totalRaised.toLocaleString('he-IL')}</span>
                <span style={{ color: '#6b7280' }}>מתוך ₪{ambassador.goal.toLocaleString('he-IL')}</span>
              </div>
              <div style={{ width: '100%', height: 20, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min(100, pct)}%`, height: '100%',
                  background: `linear-gradient(90deg, #f59e0b, #f97316)`,
                  borderRadius: 999, transition: 'width 1s ease-out',
                }} />
              </div>
              <p style={{ textAlign: 'center', marginTop: 8, fontSize: 20, fontWeight: 900, color: primary }}>{pct}%</p>
            </>
          ) : (
            <p style={{ margin: 0, textAlign: 'center', fontSize: 14, color: '#6b7280' }}>
              🎯 עדיין לא הוגדר יעד אישי
            </p>
          )}
          {ambassador.canEditGoal ? (
            <button
              onClick={openGoalEditor}
              style={{
                width: '100%', marginTop: 14, padding: '11px 0', borderRadius: 12, cursor: 'pointer',
                border: `2px solid ${primary}`, background: '#fffbeb', color: '#b45309', fontWeight: 800, fontSize: 14,
              }}
            >🎯 {ambassador.goal > 0 ? 'עדכון היעד שלי' : 'הגדרת יעד אישי'}</button>
          ) : (
            <p style={{ margin: '12px 0 0', textAlign: 'center', fontSize: 12, color: '#9ca3af' }}>
              לעדכון היעד יש לפנות לעמותה (לא רשום מספר טלפון לאימות)
            </p>
          )}
        </div>

        {/* The ambassador's group ("הקבוצה שלך") — team progress as a shared
            motivator: the sum every member is pushing together. */}
        {group && (
          <div style={{
            background: '#fff', borderRadius: 16, padding: 18, marginBottom: 16,
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #ede9fe',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              {group.imageUrl
                ? <img src={group.imageUrl} alt={group.name} style={{ width: 40, height: 40, borderRadius: 10, objectFit: (group.imageFit === 'contain' || group.imageFit === 'fill' ? group.imageFit : 'cover'), background: group.imageFit === 'contain' ? '#ede9fe' : undefined }} />
                : <span style={{ width: 40, height: 40, borderRadius: 10, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🎯</span>}
              <div>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#374151' }}>הקבוצה שלך: {group.name}</p>
                <p style={{ margin: 0, fontSize: 11, color: '#9ca3af' }}>{group.membersCount} שגרירים מגייסים יחד</p>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13, fontWeight: 700 }}>
              <span style={{ color: '#7c3aed' }}>₪{Number(group.raised).toLocaleString('he-IL')}</span>
              {group.goal > 0 && <span style={{ color: '#6b7280' }}>מתוך ₪{Number(group.goal).toLocaleString('he-IL')}</span>}
            </div>
            <div style={{ width: '100%', height: 14, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{
                width: `${Math.min(100, groupPct)}%`, height: '100%',
                background: 'linear-gradient(90deg, #7c3aed, #a78bfa)',
                borderRadius: 999, transition: 'width 1s ease-out',
              }} />
            </div>
            {group.goal > 0 && (
              <p style={{ textAlign: 'center', margin: '6px 0 0', fontSize: 15, fontWeight: 900, color: '#7c3aed' }}>{groupPct}%</p>
            )}
            <a
              href={`/campaign/${campaign.slug}/group/${group.slug}/dashboard`}
              target="_blank" rel="noreferrer"
              style={{
                display: 'block', marginTop: 10, padding: '9px', borderRadius: 10,
                background: '#f5f3ff', color: '#6d28d9', textAlign: 'center',
                textDecoration: 'none', fontWeight: 700, fontSize: 13, border: '1px solid #ddd6fe',
              }}
            >🎯 לדאשבורד הקבוצה</a>
          </div>
        )}

        {/* Stats — 2×2 grid: raised / donors on top, link views / conversion below */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '18px 14px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#059669' }}>₪{ambassador.totalRaised.toLocaleString('he-IL')}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>סה"כ גויס</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 14, padding: '18px 14px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#2563eb' }}>{ambassador.donorCount}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>תורמים</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 14, padding: '18px 14px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#7c3aed' }}>{(ambassador.visitCount ?? 0).toLocaleString('he-IL')}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>👁️ צפיות בלינק</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 14, padding: '18px 14px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#f59e0b' }}>{ambassador.conversionRate ?? 0}%</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>אחוז המרה</div>
          </div>
        </div>

        {/* Share link */}
        <div style={{
          background: '#fff', borderRadius: 14, padding: 18, marginBottom: 16,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6',
        }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 10 }}>🔗 הלינק שלי לשיתוף</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input readOnly value={shareUrl} style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 12, direction: 'ltr', color: '#6b7280' }} />
            <button
              onClick={() => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: copied ? '#22c55e' : '#f59e0b', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >{copied ? '✅ הועתק!' : '📋 העתק'}</button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <a href={`https://wa.me/?text=${encodeURIComponent(`בואו תתרמו! ${shareUrl}`)}`} target="_blank" rel="noreferrer"
              style={{ flex: 1, padding: '10px', borderRadius: 10, background: '#25D366', color: '#fff', textAlign: 'center', textDecoration: 'none', fontWeight: 700, fontSize: 13 }}>
              📱 שתף בוואטסאפ
            </a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer"
              style={{ flex: 1, padding: '10px', borderRadius: 10, background: '#1877F2', color: '#fff', textAlign: 'center', textDecoration: 'none', fontWeight: 700, fontSize: 13 }}>
              📘 שתף בפייסבוק
            </a>
          </div>
        </div>

        {/* Donations list */}
        <div style={{
          background: '#fff', borderRadius: 14, padding: 18,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6',
        }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 12 }}>💝 התרומות שהבאתי</p>
          {donations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🌱</div>
              <p style={{ fontSize: 14 }}>עדיין אין תרומות</p>
              <p style={{ fontSize: 12 }}>שתפ/י את הלינק שלך כדי להתחיל לגייס!</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {donations.map((d: any, i: number) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#f9fafb', borderRadius: 10 }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 14, color: '#374151' }}>{d.name}</span>
                    <span style={{ fontSize: 12, color: '#9ca3af', marginRight: 8 }}>{new Date(d.date).toLocaleDateString('he-IL')}</span>
                  </div>
                  <span style={{ fontWeight: 800, fontSize: 15, color: '#059669' }}>₪{d.amount.toLocaleString('he-IL')}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Goal edit modal — step 1: the new goal, step 2: the SMS code */}
        {goalOpen && (
          <div
            onClick={() => !goalBusy && setGoalOpen(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
          >
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 18, padding: 22, width: '100%', maxWidth: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
              <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 800, color: '#111827' }}>🎯 עדכון היעד האישי</h2>
              <p style={{ margin: '0 0 16px', fontSize: 12, color: '#6b7280' }}>
                כדי לשמור את היעד נשלח קוד אימות בסמס למספר שרשום אצל העמותה.
              </p>

              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>היעד שלי (₪)</label>
              <input
                type="number" min={0} inputMode="numeric"
                value={goalDraft}
                disabled={otpSent || goalBusy}
                onChange={e => setGoalDraft(e.target.value)}
                style={{
                  width: '100%', padding: '11px 12px', borderRadius: 10, border: '1px solid #e5e7eb',
                  fontSize: 16, fontWeight: 700, background: otpSent ? '#f9fafb' : '#fff', color: '#111827',
                }}
              />

              {otpSent && (
                <div style={{ marginTop: 14 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                    הקוד הועבר לספק ה-SMS עבור {otpPhoneMasked || ambassador.phoneMasked}
                  </label>
                  <p style={{ margin: '0 0 8px', fontSize: 11, color: '#6b7280' }}>
                    המסירה לטלפון עשויה להימשך מספר רגעים.
                  </p>
                  <input
                    type="text" inputMode="numeric" maxLength={6} autoFocus
                    value={otpCode}
                    onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="------"
                    style={{
                      width: '100%', padding: '11px 12px', borderRadius: 10, border: '1px solid #e5e7eb',
                      fontSize: 22, fontWeight: 800, letterSpacing: 6, textAlign: 'center', direction: 'ltr', color: '#111827',
                    }}
                  />
                  <button
                    onClick={requestOtp} disabled={goalBusy}
                    style={{ marginTop: 8, background: 'none', border: 'none', color: '#2563eb', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}
                  >שלחו לי קוד חדש</button>
                </div>
              )}

              {goalError && (
                <p style={{ margin: '12px 0 0', fontSize: 13, color: '#dc2626', fontWeight: 600 }}>{goalError}</p>
              )}
              {goalDone && (
                <p style={{ margin: '12px 0 0', fontSize: 13, color: '#059669', fontWeight: 700 }}>✅ היעד עודכן!</p>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                <button
                  onClick={() => setGoalOpen(false)} disabled={goalBusy}
                  style={{ flex: 1, padding: '11px 0', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
                >ביטול</button>
                <button
                  onClick={otpSent ? confirmGoal : requestOtp}
                  disabled={goalBusy || goalDone || (otpSent && otpCode.length !== 6)}
                  style={{
                    flex: 2, padding: '11px 0', borderRadius: 10, border: 'none', color: '#fff', fontWeight: 800, fontSize: 14,
                    background: goalBusy || goalDone || (otpSent && otpCode.length !== 6) ? '#d1d5db' : primary,
                    cursor: goalBusy ? 'wait' : 'pointer',
                  }}
                >{goalBusy ? 'רגע...' : otpSent ? 'אישור ועדכון' : '📩 שליחת קוד בסמס'}</button>
              </div>

              <p style={{ margin: '14px 0 0', fontSize: 11, color: '#9ca3af', textAlign: 'center' }}>
                מספר הטלפון ניתן לשינוי ע"י העמותה בלבד.
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '24px 0 12px', color: '#9ca3af', fontSize: 11 }}>
          <p>מופעל ע"י <strong style={{ color: '#6b7280' }}>Amutech</strong> — משקיעים באנשים שעושים טוב</p>
        </div>
      </div>
    </div>
  );
};

export default PublicAmbassadorDashboard;
